import React from 'react';
import DespatchConfirmationDialog from './DespatchConfirmationDialog';
import FreightChargesDialog from './FreightChargesDialog';
import SalesSurchargeDialog from './SalesSurchargeDialog';
import DespatchDetailsDialog from './DespatchDetailsDialog';
import { usePickSlipConfirmation, useProcessPickSlip, useRemoveInvoiceChargeLines } from 'api/pickSlips/pickslip';
import { isScreenRequest } from 'api/utils';
import { IProcessPickSlipDialogsProperties } from './ProcessPickSlipDialogs.properties';
import PaymentEntryWrapper from 'components/common/PaymentEntryWrapper';
import { INotificationFacade, IProcessPaymentDetailsResponse } from 'api/swaggerTypes';
import LocalShipmentDialog from 'components/common/LocalShipmentDialog';
import LocalShipmentOutputDialog from 'components/common/LocalShipmentOutputDialog';
import InternalOrderDialog from 'components/Worksale/BackorderCommitment/InternalOrderDialog';
import { useSendNotification } from 'api/documentOutput';

const ProcessPickSlipDialogs = (props: IProcessPickSlipDialogsProperties) => {
    const { selectedDespatchId, linesRef, summaryRef, forwardedRef } = props;

    const [freightChargesDialogOpen, setFreightChargesDialogOpen] = React.useState<boolean>(false);
    const [salesSurchargeDialogOpen, setSalesSurchargeDialogOpen] = React.useState<boolean>(false);
    const [despatchDetailsDialogOpen, setDespatchDetailsDialogOpen] = React.useState<boolean>(false);
    const [despatchConfirmationOpen, setDespatchConfirmationOpen] = React.useState(false);
    const [paymentEntryOpen, setPaymentEntryOpen] = React.useState<boolean>(false);
    const [localShipmentDialogOpen, setLocalShipmentDialogOpen] = React.useState<boolean>(false);
    const [localShipmentOutputDialogOpen, setLocalShipmentOutputDialogOpen] = React.useState<boolean>(false);
    const [internalOrderNotificationDialogOpen, setInternalOrderNotificationDialogOpen] = React.useState<boolean>(false);
    const [InternalOrderNotificationData, setInternalOrderNotificationData] = React.useState<any>(null);

    const confirmPickSlipMutation = usePickSlipConfirmation();
    const processPickSlipMutation = useProcessPickSlip();
    const removeInvoiceChargeLinesMutation = useRemoveInvoiceChargeLines();
    const sendNotificationMutation = useSendNotification();

    const processDespatchCriteria = processPickSlipMutation.data?.ProcessDespatchCriteria?.inlineObject;

    const processPickSlipOnClick = React.useCallback(
        async () => {
            const response = await confirmPickSlipMutation.mutateAsync({
                DespatchId: selectedDespatchId
            });

            if (response?.Status === true) {
                setDespatchConfirmationOpen(true);
            }
        },
        [confirmPickSlipMutation, selectedDespatchId]
    );

    React.useImperativeHandle(
        forwardedRef,
        () => ({
            process: processPickSlipOnClick,
        }),
        [processPickSlipOnClick]
    );

    const processPickSlip = React.useCallback(
        async (data) => {
            const response = await processPickSlipMutation.mutateAsync({
                ...data,
                FreightCharge: data?.FreightCharge?.inlineObject ?? data?.FreightCharge,
                SalesSurcharge: data?.SalesSurcharge?.inlineObject ?? data?.SalesSurcharge,
                DespatchDetail: data?.DespatchDetail?.inlineObject ?? data?.DespatchDetail
            });

            if (isScreenRequest(response?.Forms, 'FreightCharges')) {
                setDespatchConfirmationOpen(false);
                setFreightChargesDialogOpen(true);

                return;
            }
            if (isScreenRequest(response?.Forms, 'SalesSurcharge')) {
                setDespatchConfirmationOpen(false);
                setSalesSurchargeDialogOpen(true);

                return;
            }
            if (isScreenRequest(response?.Forms, 'DespatchDetails')) {
                setDespatchConfirmationOpen(false);
                setDespatchDetailsDialogOpen(true);

                return;
            }
            if (isScreenRequest(response?.Forms, 'PaymentEntry')) {
                setDespatchConfirmationOpen(false);
                setPaymentEntryOpen(true);

                return;
            }
            if (isScreenRequest(response?.Forms, 'LocalShipmentEntry')) {
                setDespatchConfirmationOpen(false);
                setLocalShipmentDialogOpen(true);
            }
            if (isScreenRequest(response?.Forms, 'LocalShipmentDespatchOutput')) {
                setDespatchConfirmationOpen(false);
                setLocalShipmentOutputDialogOpen(true);
            }

            if (isScreenRequest(response?.Forms, 'NotificationDialog')) {
                setInternalOrderNotificationData(response?.ProcessDespatchCriteria?.inlineObject);
                setDespatchConfirmationOpen(false);
                setInternalOrderNotificationDialogOpen(true);
            }

            if (response?.Status === true && response?.Forms?.[0]?.MessageType === 'Success') {
                setDespatchConfirmationOpen(false);
                linesRef.current?.refreshDataGrid();
                summaryRef.current?.reloadSummary();
            }
        },
        [linesRef, summaryRef]
    );

    const freightChargeOnOk = React.useCallback(
        (values) => {
            processPickSlip({
                ...processDespatchCriteria,
                FreightCharge: values
            });
            setFreightChargesDialogOpen(false);
        },
        [processPickSlipMutation.data, processPickSlip]
    );
    const salesChargeOnOk = React.useCallback(
        (values) => {
            processPickSlip({
                ...processDespatchCriteria,
                SalesSurcharge: values
            });
            setSalesSurchargeDialogOpen(false);
        },
        [processPickSlipMutation.data, processPickSlip]
    );

    const despatchDetailsOnOk = React.useCallback(
        (values) => {
            processPickSlip({
                ...processDespatchCriteria,
                DespatchDetail: values
            });
            setDespatchDetailsDialogOpen(false);
        },
        [processPickSlipMutation.data, processPickSlip]
    );

    const localShipmentEntryOnOk = React.useCallback(
        (values) => {
            processPickSlip({
                ...processDespatchCriteria,
                LocalShipment: values
            });
            setLocalShipmentDialogOpen(false);
        },
        [processPickSlipMutation.data, processPickSlip]
    );

    const localShipmentOutputOnOk = React.useCallback(
        (values) => {
            processPickSlip({
                ...processDespatchCriteria,
                LocalShipmentOut: values,
                OutputLocalShipmentDespatch: true,
            });
            setLocalShipmentOutputDialogOpen(false);
        },
        [processPickSlipMutation.data, processPickSlip]
    );

    // Notification after processing
    const sendNotificationonOk = React.useCallback(
        async (data: INotificationFacade) => {
            const response = await sendNotificationMutation.mutateAsync({
                ...data
            });

            if (response?.Status) {
                setInternalOrderNotificationDialogOpen(false);
            }
        },
        []
    );

    const handleApplyPaymentEntry = React.useCallback(
        async (paymentResponse: IProcessPaymentDetailsResponse) => {
            if (paymentResponse.Status) {
                setPaymentEntryOpen(false);
                processPickSlip({
                    ...processPickSlipMutation?.data?.ProcessDespatchCriteria?.inlineObject,
                    PaymentDetail: paymentResponse?.ProcessPaymentDetail
                });
            }
        },
        [setPaymentEntryOpen, processPickSlip, processPickSlipMutation?.data]
    );

    const removeInvoiceChargeLines = React.useCallback(
        () => {
            removeInvoiceChargeLinesMutation.mutate({
                InvoiceNumber: processDespatchCriteria?.Invoice,
                SalesEntity: processDespatchCriteria?.SalesEntity,
                urlQueryParams: {
                    FreightLineNumber: processDespatchCriteria?.FreightLineNumber
                }
            });
        },
        [processPickSlipMutation.data]
    );

    const freightChargesOnCancel = React.useCallback(
        () => {
            removeInvoiceChargeLines();
            setFreightChargesDialogOpen(false);
        },
        [removeInvoiceChargeLines]
    );
    const salesSurchargeOnCancel = React.useCallback(
        () => {
            removeInvoiceChargeLines();
            setSalesSurchargeDialogOpen(false);
        },
        [removeInvoiceChargeLines]
    );
    const despatchDetailsOnCancel = React.useCallback(
        () => {
            removeInvoiceChargeLines();
            setDespatchDetailsDialogOpen(false);
        },
        [removeInvoiceChargeLines]
    );
    const paymentEntryOnCancel = React.useCallback(
        async () => {
            removeInvoiceChargeLines();
            setPaymentEntryOpen(false);
        },
        [removeInvoiceChargeLines]
    );

    return (
        <>
            <DespatchConfirmationDialog
                open={despatchConfirmationOpen}
                initialValues={confirmPickSlipMutation.data?.DespatchConfirmation?.inlineObject}
                valuesSchema={confirmPickSlipMutation.data?.DespatchConfirmation?.schema}
                isProcessing={processPickSlipMutation.isLoading}
                onCancel={() => { setDespatchConfirmationOpen(false); }}
                onOk={processPickSlip}
                customerId={Number(summaryRef.current?.getSummary()?.CustomerId) || 0}
            />
            <FreightChargesDialog
                open={freightChargesDialogOpen}
                data={processDespatchCriteria?.FreightCharge}
                isProcessing={processPickSlipMutation.isLoading}
                onOk={freightChargeOnOk}
                onCancel={freightChargesOnCancel} />
            <SalesSurchargeDialog
                open={salesSurchargeDialogOpen}
                data={processDespatchCriteria?.SalesSurcharge}
                isProcessing={processPickSlipMutation.isLoading}
                onOk={salesChargeOnOk}
                onCancel={salesSurchargeOnCancel} />
            <DespatchDetailsDialog
                open={despatchDetailsDialogOpen}
                data={processDespatchCriteria?.DespatchDetail}
                isProcessing={processPickSlipMutation.isLoading}
                onOk={despatchDetailsOnOk}
                onCancel={despatchDetailsOnCancel} />
            <PaymentEntryWrapper
                open={paymentEntryOpen}
                initialValues={processPickSlipMutation?.data?.ProcessDespatchCriteria?.inlineObject?.PaymentInformation}
                isProcessing={processPickSlipMutation.isLoading}
                onCancel={paymentEntryOnCancel}
                onApply={handleApplyPaymentEntry}
            />
            <LocalShipmentDialog
                open={localShipmentDialogOpen}
                onClose={() => { setLocalShipmentDialogOpen(false); }}
                onOk={localShipmentEntryOnOk}
                initialValues={(processPickSlipMutation?.data?.ProcessDespatchCriteria?.inlineObject as any)?.LocalShipment}
            />

            <LocalShipmentOutputDialog
                open={localShipmentOutputDialogOpen}
                onCancel={() => { setLocalShipmentOutputDialogOpen(false); }}
                initialValues={processPickSlipMutation.data?.ProcessDespatchCriteria?.inlineObject}
                onOk={localShipmentOutputOnOk}
                customerId={Number(summaryRef.current?.getSummary()?.CustomerId) || 0}
            />

            <InternalOrderDialog
                open={internalOrderNotificationDialogOpen}
                value={InternalOrderNotificationData?.Notification}
                valueSchema={{}}
                onCancel={() => setInternalOrderNotificationDialogOpen(false)}
                onOk={sendNotificationonOk}
            />
        </>
    );
};

export default ProcessPickSlipDialogs;
